import { Mail, Smartphone } from 'lucide-react';

/** Shown after "Signing in from outside Canada?" — not a separate location screen. */
export type InternationalStep = 'canadian_phone';

interface LoginInternationalPathProps {
  step: InternationalStep | null;
  onOpen: () => void;
  onUsePhoneLogin: () => void;
  onUseEmailLogin: () => void;
}

const choiceCardClass =
  'w-full flex items-center gap-4 md:gap-5 px-5 md:px-6 py-4 md:py-5 bg-gray-50/80 rounded-xl border border-gray-200 shadow-sm hover:bg-white hover:shadow-md transition-all group dark:bg-white/5 dark:border-white/10';

export function LoginInternationalPath({
  step,
  onOpen,
  onUsePhoneLogin,
  onUseEmailLogin,
}: LoginInternationalPathProps) {
  if (!step) {
    return (
      <div className="pt-2 text-center">
        <button
          type="button"
          onClick={onOpen}
          className="text-xs text-gray-500 underline-offset-2 hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
        >
          Signing in from outside Canada?
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4">
      <p className="text-sm md:text-base text-center font-semibold text-gray-900 dark:text-white px-1">
        Do you have a Canadian phone number?
      </p>

      <button
        type="button"
        onClick={onUsePhoneLogin}
        className={`${choiceCardClass} hover:border-red-300 dark:hover:border-red-500/50 dark:hover:bg-white/10`}
      >
        <div className="w-11 h-11 md:w-14 md:h-14 rounded-full bg-red-50 flex items-center justify-center shrink-0 group-hover:bg-red-100 transition-colors dark:bg-red-900/30 dark:group-hover:bg-red-900/50">
          <Smartphone className="w-5 h-5 md:w-6 md:h-6 text-red-700 dark:text-red-400" />
        </div>
        <div className="text-left min-w-0 flex-1">
          <span className="block text-sm md:text-base lg:text-lg font-semibold text-gray-900 dark:text-white">
            Yes
          </span>
          <span className="block text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-0.5 md:mt-1">
            Continue with phone — we&apos;ll text you a verification code
          </span>
        </div>
      </button>

      <button
        type="button"
        onClick={onUseEmailLogin}
        className={`${choiceCardClass} hover:border-indigo-300 dark:hover:border-indigo-500/50 dark:hover:bg-white/10`}
      >
        <div className="w-11 h-11 md:w-14 md:h-14 rounded-full bg-indigo-50 flex items-center justify-center shrink-0 group-hover:bg-indigo-100 transition-colors dark:bg-indigo-900/30 dark:group-hover:bg-indigo-900/50">
          <Mail className="w-5 h-5 md:w-6 md:h-6 text-indigo-700 dark:text-indigo-400" />
        </div>
        <div className="text-left min-w-0 flex-1">
          <span className="block text-sm md:text-base lg:text-lg font-semibold text-gray-900 dark:text-white">
            Use email
          </span>
          <span className="block text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-0.5 md:mt-1">
            Verify with the email address we already have on file
          </span>
        </div>
      </button>
    </div>
  );
}
